import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, EMPTY, of, switchMap, tap } from 'rxjs';

import { AuthHttpService } from './auth-http.service';
import { defaultUser, type UserSignIn, type UserSignUpDto, type UserUpdate } from './user.model';
import { TokenStorageService } from './token-storage.service';
import { jwtDecode } from 'jwt-decode';
import { ErrorHandlerService } from 'app/shared/error-handler.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private router = inject(Router);
  private httpService = inject(AuthHttpService);
  private tokenService = inject(TokenStorageService);
  private errorService = inject(ErrorHandlerService);
  private _user = signal<UserSignIn | null>(null);

  _resetPasswordRequestId: string | null = null;
  user = computed(() => (this._user() ? (this._user() as UserSignIn) : defaultUser));

  constructor() {
    this._user.set(this.tokenService.getUser());
  }

  signUp(userSignUp: UserSignUpDto) {
    return this.httpService.signUp(userSignUp).pipe(
      tap((user) => this._user.set(user)),
      switchMap(() => this.signIn(userSignUp.email, userSignUp.password)),
    );
  }

  signIn(email: string, password: string, remember = false) {
    return this.httpService.signIn(email, password).pipe(
      tap((resData) => {
        this._user.set(resData.user);

        this.tokenService.saveToken(resData.accessToken);
        this.tokenService.saveUser(resData.user);
        if (remember) {
          this.tokenService.saveRefreshToken(resData.refreshToken);
        }
      }),
      catchError(() => this.errorService.handleKnownError('signIn')),
    );
  }

  updateUser(userUpdate: Partial<UserUpdate>) {
    return this.httpService.updateUser(userUpdate).pipe(
      tap(() => this._user.update((oldUser) => ({ ...(oldUser as UserSignIn), ...userUpdate }))),
      tap(() => this.tokenService.saveUser(this.user())),
    );
  }

  logout() {
    return this.httpService.logout().pipe(
      tap(() => {
        this.tokenService.logout();
        this.router.navigate(['/login']);
      }),
      catchError(() => {
        this.tokenService.logout();
        this.router.navigate(['/login']);
        return of(null);
      }),
    );
  }

  refreshAccessToken(token: string) {
    return this.httpService.refreshAccessToken(token);
  }

  getAuthorizedUser() {
    return this.httpService.getAuthorizedUser();
  }

  deleteAccount() {
    return this.httpService.deleteAccount().pipe(tap(() => this.tokenService.logout()));
  }

  forgotPassword(method: 'email' | 'phone', address: string) {
    return this.httpService
      .forgotPassword(method, address)
      .pipe(tap((resData) => (this._resetPasswordRequestId = resData.requestId)));
  }

  confirmResetPassword(code: string) {
    if (!this._resetPasswordRequestId) {
      return EMPTY;
    }
    return this.httpService.confirmResetPassword(this._resetPasswordRequestId, code);
  }

  resendConfirmationCode() {
    if (!this._resetPasswordRequestId) {
      return EMPTY;
    }
    return this.httpService.resendConfirmationCode(this._resetPasswordRequestId);
  }

  setNewPassword(newPassword: string) {
    if (!this._resetPasswordRequestId) {
      return EMPTY;
    }
    return this.httpService.setNewPassword(this._resetPasswordRequestId, newPassword);
  }

  changePassword(oldPassword: string, newPassword: string) {
    return this.httpService.changePassword(oldPassword, newPassword).pipe(
      tap(() => {
        this.tokenService.logout();
        this.router.navigate(['/login']);
      }),
    );
  }

  isTokenValid(token: string | null): boolean {
    if (!token) {
      return false;
    }
    try {
      const decodedToken = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      return !!(decodedToken && decodedToken.exp && decodedToken.exp > currentTime);
    } catch (error) {
      console.error('Failed to decode jwt token:', error);
    }
    return false;
  }
}
